// Streaming port of the motion-test benchmark's v2 activation model: motion
// arms on N consecutive strong frames and, once armed, is held by hysteresis —
// frames above half the threshold keep it alive. The benchmark's offline-only
// parts (interval backdating, gap merging) need future knowledge and are
// omitted; in realtime the release delay plus hysteresis bridge short lulls.

// Once armed, motion stays alive while the signal holds above this share of
// the threshold, so sub-threshold lulls inside one event do not split it.
const HYSTERESIS_RATIO = 0.5;
// Pre-arm: weak frames hold an arming streak only this long after the last
// strong frame, so sparse noise spikes cannot slowly accumulate into an arm.
const WEAK_HOLD_MS = 1000;
// Weak frames alone extend the release by at most this beyond the last strong
// frame, so periodic sub-threshold flicker (burnt-in clocks) cannot keep an
// event alive forever.
const WEAK_EXTENSION_MS = 2000;

type ActivationModelParams = {
  armFrames: number;
  releaseDelayMs: number;
};

export default class ActivationModel {
  private armFrames: number;
  private releaseDelayMs: number;

  private streak = 0;
  private lastStrongMs: number | null = null;
  private lastWeakMs: number | null = null;
  private armed = false;

  constructor({ armFrames, releaseDelayMs }: ActivationModelParams) {
    this.armFrames = armFrames;
    this.releaseDelayMs = releaseDelayMs;
  }

  reset = () => {
    this.streak = 0;
    this.lastStrongMs = null;
    this.lastWeakMs = null;
    this.armed = false;
  };

  /**
   * Feed one frame's signal and threshold. Suppressed frames (warm-up, scene
   * change) count as fully inactive regardless of signal. Returns whether the
   * frame is inside motion.
   */
  update = (signal: number, threshold: number, timestampMs: number, suppressed = false) => {
    const strong = !suppressed && signal > threshold;
    const weak = !suppressed && signal > threshold * HYSTERESIS_RATIO;

    if (strong) this.lastStrongMs = timestampMs;
    if (weak) this.lastWeakMs = timestampMs;

    if (!this.armed) {
      if (strong) {
        this.streak += 1;
        if (this.streak >= this.armFrames) this.armed = true;
      } else if (!weak || this.lastStrongMs === null || timestampMs - this.lastStrongMs > WEAK_HOLD_MS) {
        this.streak = 0;
      }
      return this.armed;
    }

    if (!weak) {
      const sinceWeak = this.lastWeakMs === null ? Infinity : timestampMs - this.lastWeakMs;
      const sinceStrong = this.lastStrongMs === null ? Infinity : timestampMs - this.lastStrongMs;
      if (sinceWeak >= this.releaseDelayMs || sinceStrong >= this.releaseDelayMs + WEAK_EXTENSION_MS) {
        this.armed = false;
        this.streak = 0;
      }
    } else if (
      !strong &&
      this.lastStrongMs !== null &&
      timestampMs - this.lastStrongMs >= this.releaseDelayMs + WEAK_EXTENSION_MS
    ) {
      this.armed = false;
      this.streak = 0;
    }
    return this.armed;
  };
}
